// HoverPortalDropdown.jsx
import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Download } from "lucide-react";

/**
 * HoverPortalDropdown
 * - Рендерит кнопку внутри списка (как раньше).
 * - Само меню рендерится в document.body (портал) — не обрезается overflow родителя.
 * - Открывается ПО НАВЕДЕНИЮ (hover) и открывается строго ВНИЗ (без флипа).
 * - Закрывается при уходе курсора с кнопки и меню (с небольшой задержкой чтобы избежать мерцания).
 *
 * Props:
 *  - protocol
 *  - downloadProtocol(protocol, format)
 *  - menuWidth (px) — по умолчанию 128 (w-32)
 *  - openDelay, closeDelay (ms)
 */
export default function HoverPortalDropdown({
  protocol,
  downloadProtocol,
  menuWidth = 128,
  openDelay = 100,
  closeDelay = 150,
}) {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Определяем, на тач-устройствах будем использовать клик (hover там не работает)
  const isTouchDevice = typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  const computePosition = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;

    // Жёстко ставим меню ПОД кнопкой (без флипа)
    const left = rect.right - menuWidth + scrollX; // align end (правый край совпадает)
    const top = rect.bottom + 8 + scrollY; // mt-2 ~= 8px

    setPos({ top, left });
  };

  // Открыть с задержкой
  const scheduleOpen = () => {
    clearTimeout(closeTimer.current);
    clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => {
      computePosition();
      setOpen(true);
    }, openDelay);
  };

  // Закрыть с задержкой
  const scheduleClose = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), closeDelay);
  };

  // Очистка таймеров при размонтировании
  useEffect(() => {
    return () => {
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
    };
  }, []);

  // Когда меню открыто — следим за скроллом/resize чтобы позиция оставалась корректной
  useEffect(() => {
    if (!open) return;
    const onScroll = () => computePosition();
    const onResize = () => computePosition();
    window.addEventListener("scroll", onScroll, true); // true — чтобы ловить прокрутку в scroller'ах
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  // Если устройство тач — переключаем на клик (toggle) поведение
  if (isTouchDevice) {
    return (
      <>
        <button
          ref={btnRef}
          onClick={() => {
            if (open) {
              setOpen(false);
            } else {
              computePosition();
              setOpen(true);
            }
          }}
          className="btn-primary flex items-center space-x-1"
          type="button"
          aria-expanded={open}
        >
          <Download className="h-4 w-4" />
          <span>Скачать</span>
        </button>

        {open &&
          createPortal(
            <div
              ref={menuRef}
              style={{
                position: "absolute",
                top: `${pos.top}px`,
                left: `${pos.left}px`,
                width: `${menuWidth}px`,
                zIndex: 9999,
              }}
              className="bg-white rounded-lg shadow-lg border border-secondary-200"
              role="menu"
            >
              <button
                onClick={() => {
                  downloadProtocol(protocol, 'docx');
                  // даём 400ms на то, чтобы диалог скачивания/фокус сработал
                  setTimeout(() => setOpen(false), 400);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100 rounded-t-lg"
              >
                Скачать DOCX
              </button>
              <button
                onClick={() => {
                  downloadProtocol(protocol, 'pdf');
                  // даём 400ms на то, чтобы диалог скачивания/фокус сработал
                  setTimeout(() => setOpen(false), 400);
                }}
                className="block w-full text-left px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100 rounded-b-lg"
              >
                Скачать PDF
              </button>
            </div>,
            document.body
          )}
      </>
    );
  }

  // Десктоп/ноут — hover поведение
  return (
    <>
      <div
        // wrapper ловит enter/leave над кнопкой
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={scheduleOpen}
        onBlur={scheduleClose}
        style={{ display: "inline-block" }}
      >
        <button
          ref={btnRef}
          type="button"
          className="btn-primary flex items-center space-x-1"
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <Download className="h-4 w-4" />
          <span>Скачать</span>
        </button>
      </div>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            onMouseEnter={() => {
              // если курсор переместился на меню — отменяем закрытие
              clearTimeout(closeTimer.current);
            }}
            onMouseLeave={scheduleClose}
            style={{
              position: "absolute",
              top: `${pos.top}px`,
              left: `${pos.left}px`,
              width: `${menuWidth}px`,
              zIndex: 9999,
            }}
            className="bg-white rounded-lg shadow-lg border border-secondary-200"
            role="menu"
          >
            <button
              onClick={() => { downloadProtocol(protocol, "docx"); setOpen(false); }}
              className="block w-full text-left px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100 rounded-t-lg"
              role="menuitem"
            >
              Скачать DOCX
            </button>
            <button
              onClick={() => { downloadProtocol(protocol, "pdf"); setOpen(false); }}
              className="block w-full text-left px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100 rounded-b-lg"
              role="menuitem"
            >
              Скачать PDF
            </button>
          </div>,
          document.body
        )}
    </>
  );
}